package org.example.project.engineservice.schulung

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object SchulungApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    private fun currentFirmenId(): String =
        LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }

    private fun triggerSchulungRefresh(includeKalender: Boolean = false) {
        GlobalManager.markJsonChanged("schulung", "schulung/schulung.json")
        if (includeKalender) {
            GlobalManager.markJsonChanged("kalender", "user/~/termine/schulung.json")
        }
    }

    suspend fun listSchulungen(): List<All> =
        decodeList(LumaticaApiManager.call("schulung/list/${apiPart(currentFirmenId())}"))

    suspend fun listUsers(): List<All> =
        decodeList(LumaticaApiManager.call("schulung/user-list/${apiPart(currentFirmenId())}"))

    suspend fun listTeilnehmer(schulungId: String): List<All> =
        decodeList(LumaticaApiManager.call("schulung/teilnehmer/${apiPart(currentFirmenId())}/${apiPart(schulungId)}"))

    suspend fun searchSchulungen(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("schulung/search-tabular", filters))

    suspend fun searchTeilnehmer(schulungId: String, filters: Map<String, String>): List<All> =
        decodeList(
            loadWithTabularFilters(
                "schulung/teilnehmer-search-tabular",
                filters,
                mapOf("schulungId" to schulungId)
            )
        )

    suspend fun myTeilnahmen(): List<All> =
        decodeList(LumaticaApiManager.call("schulung/my/${apiPart(currentFirmenId())}/${apiPart(LoginService.getId())}"))

    suspend fun document(schulungId: String): All? =
        decodeOne(LumaticaApiManager.call("schulung/document/${apiPart(currentFirmenId())}/${apiPart(schulungId)}"))

    suspend fun loadKiSettings(): String {
        val raw = LumaticaApiManager.call("schulung/settings/${apiPart(currentFirmenId())}")
        return runCatching {
            json.parseToJsonElement(raw).jsonObject["settings"]?.jsonPrimitive?.content.orEmpty()
        }.getOrDefault("")
    }

    suspend fun saveKiSettings(settings: String): String {
        val body = buildJsonObject {
            put("firmenId", currentFirmenId())
            put("settings", settings)
        }
        val raw = postRaw("settings-save/${apiPart(currentFirmenId())}", body)
        val saved = runCatching {
            json.parseToJsonElement(raw).jsonObject["settings"]?.jsonPrimitive?.content.orEmpty()
        }.getOrDefault("")
        if (saved.isNotBlank()) triggerSchulungRefresh()
        return saved
    }

    suspend fun createSchulung(
        name: String,
        typ: String,
        anweisung: String,
        dokumentName: String,
        dokumentPdfDataUrl: String,
        modus: String,
        datum: String,
        uhrzeitVon: String,
        uhrzeitBis: String,
        standort: String,
        aktivVon: String,
        aktivBis: String
    ): All? {
        val body = buildJsonObject {
            put("firmenId", currentFirmenId())
            put("userId", LoginService.getId())
            put("name", name)
            put("typ", typ)
            put("anweisung", anweisung)
            put("dokumentName", dokumentName)
            put("dokumentPdfDataUrl", dokumentPdfDataUrl)
            put("modus", modus)
            put("datum", datum)
            put("uhrzeitVon", uhrzeitVon)
            put("uhrzeitBis", uhrzeitBis)
            put("standort", standort)
            put("aktivVon", aktivVon)
            put("aktivBis", aktivBis)
        }
        val saved = postOne("create/${apiPart(currentFirmenId())}/${apiPart(LoginService.getId())}", body)
        if (saved != null) triggerSchulungRefresh(includeKalender = modus.equals("PHYSISCH", ignoreCase = true))
        return saved
    }

    suspend fun saveSchulung(
        schulungId: String,
        name: String,
        typ: String,
        anweisung: String,
        modus: String,
        datum: String,
        uhrzeitVon: String,
        uhrzeitBis: String,
        standort: String,
        aktivVon: String,
        aktivBis: String,
        dokumentName: String? = null,
        dokumentPdfDataUrl: String? = null
    ): All? {
        val body = buildJsonObject {
            put("firmenId", currentFirmenId())
            put("schulungId", schulungId)
            put("userId", LoginService.getId())
            put("name", name)
            put("typ", typ)
            put("anweisung", anweisung)
            put("modus", modus)
            put("datum", datum)
            put("uhrzeitVon", uhrzeitVon)
            put("uhrzeitBis", uhrzeitBis)
            put("standort", standort)
            put("aktivVon", aktivVon)
            put("aktivBis", aktivBis)
            dokumentName?.let { put("dokumentName", it) }
            dokumentPdfDataUrl?.let { put("dokumentPdfDataUrl", it) }
        }
        val saved = postOne("save/${apiPart(currentFirmenId())}/${apiPart(schulungId)}/${apiPart(LoginService.getId())}", body)
        if (saved != null) triggerSchulungRefresh(includeKalender = modus.equals("PHYSISCH", ignoreCase = true))
        return saved
    }

    suspend fun deleteSchulung(schulungId: String): Boolean {
        val body = buildJsonObject {
            put("firmenId", currentFirmenId())
            put("schulungId", schulungId)
        }
        val raw = postRaw("delete/${apiPart(currentFirmenId())}/${apiPart(schulungId)}", body)
        val saved = raw.contains("\"ok\":true") || raw.contains("\"deleted\":true")
        if (saved) triggerSchulungRefresh(includeKalender = true)
        return saved
    }

    suspend fun planSchulung(
        schulungId: String,
        userIds: List<String>,
        modus: String,
        datum: String,
        uhrzeitVon: String,
        uhrzeitBis: String,
        standort: String,
        aktivVon: String,
        aktivBis: String
    ): List<All> {
        val body = buildJsonObject {
            put("firmenId", currentFirmenId())
            put("schulungId", schulungId)
            put("modus", modus)
            put("datum", datum)
            put("uhrzeitVon", uhrzeitVon)
            put("uhrzeitBis", uhrzeitBis)
            put("standort", standort)
            put("aktivVon", aktivVon)
            put("aktivBis", aktivBis)
            put("erstelltVon", LoginService.getId())
            put("userIds", buildJsonArray { userIds.forEach { add(JsonPrimitive(it)) } })
        }
        val saved = postList("plan/${apiPart(currentFirmenId())}/${apiPart(schulungId)}", body)
        if (saved.isNotEmpty()) triggerSchulungRefresh(includeKalender = modus.equals("PHYSISCH", ignoreCase = true))
        return saved
    }

    suspend fun start(schulungId: String, provider: String): All? {
        val body = buildJsonObject {
            put("firmenId", currentFirmenId())
            put("schulungId", schulungId)
            put("userId", LoginService.getId())
            put("provider", provider)
        }
        val saved = postOne("start/${apiPart(currentFirmenId())}/${apiPart(schulungId)}/${apiPart(LoginService.getId())}", body)
        if (saved != null) triggerSchulungRefresh()
        return saved
    }

    suspend fun finish(schulungId: String, provider: String, abschlussbericht: String): All? {
        val body = buildJsonObject {
            put("firmenId", currentFirmenId())
            put("schulungId", schulungId)
            put("userId", LoginService.getId())
            put("provider", provider)
            put("abschlussbericht", abschlussbericht)
        }
        val saved = postOne("finish/${apiPart(currentFirmenId())}/${apiPart(schulungId)}/${apiPart(LoginService.getId())}", body)
        if (saved != null) triggerSchulungRefresh()
        return saved
    }

    private suspend fun postOne(actionPath: String, body: kotlinx.serialization.json.JsonObject): All? =
        decodeOne(postRaw(actionPath, body))

    private suspend fun postList(actionPath: String, body: kotlinx.serialization.json.JsonObject): List<All> =
        decodeList(postRaw(actionPath, body))

    private suspend fun postRaw(actionPath: String, body: kotlinx.serialization.json.JsonObject): String {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/schulung/$actionPath",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(body)
            )
        ).await()
        val raw = response.text().await()
        if (!response.ok) console.error("Schulung API failed", response.status, raw)
        return raw
    }

    private fun decodeList(raw: String): List<All> =
        runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
